/* LEGAL-NOTICE
   © Copyright 2018 - Quid Informatica S.p.A. - All Rights Reserved
   https://quidinfo.it/qinetic/licence.html - "Quid Consumer Lending Platform"
   Deposito SIAE n. 012702 del 26 Luglio 2018
LEGAL-NOTICE */
import { environment } from '@environments/environment';
import { map } from 'rxjs/operators';
import { ApiResParserService } from './../api-res-parser/api-res-parser.service';
import { Options } from './../../models/options';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  ChecklistDTO,
  SurveyUpdateDTO,
  PositionDTO,
  WizardDTO,
  SurveyDTO,
  FormDTO,
  DomainListDTO,
  IdentificationLinkDTO,
  FormUpdateDTO,
  LabelListDTO,
  UserDataDTO,
  WizardStepNextOutputDTO,
  ReportRequestDTO,
  AuthenticateDTO,
  IdentificationVerifyDTO,
  HawkScanDTO,
  DigitalSigningDTO,
  QuotationDTO,
  QuotationParamsDTO,
  BankAccountLinkDTO,
  BankAccountEvaluationDTO,
  NoteDTO,
  UserCustomerDTO,
  UserCustomerUpdateDTO,
  AtcDTO,
  PositionSaveRequestDTO,
  NotificationDTO,
  CreditRequestDTO,
  QuotationBoundariesDTO,
  CreditUseListDTO,
  PostsellingCreateDTO,
  TaskListDTO,
  ReportDTO,
  LogRequestDTO
} from '../../models';

@Injectable()
export class ApiService {
  protected baseUrl = environment.baseUrl;

  protected apiUrl = environment.apiBaseUrl;

  protected securityUrl = environment.securityBaseUrl;

  constructor(
    protected http: HttpClient,
    protected parser: ApiResParserService
  ) {}

  putLog(body: LogRequestDTO, options?: Options): Observable<any> {
    const url = `${this.apiUrl}/v1/user/action`;

    return this.http.put<any>(url, body, options);
  }

  getLabels(options?: Options): Observable<LabelListDTO> {
    const url = `${this.apiUrl}/v1/labels`;
    return this.http.get<LabelListDTO>(url, options);
  }

  getDomain(code: string, options?: Options): Observable<DomainListDTO> {
    const url = `${this.apiUrl}/v1/domains/${code}`;
    return this.http.get<DomainListDTO>(url, options);
  }

  postSecurityLogon(req: AuthenticateDTO, options?: Options): Observable<any> {
    return this.http.post<void>(
      `${this.securityUrl}/authenticate`,
      req,
      options
    );
  }

  getSecurityVerify(options?: Options): Observable<void> {
    return this.http.get<void>(`${this.securityUrl}/verify`, options);
  }

  getSecurityUserData(options?: Options): Observable<UserDataDTO> {
    return this.http
      .get<UserDataDTO>(`${this.securityUrl}/userdata`, options)
      .pipe(map(res => this.parser.userData(res)));
  }

  postSecurityLogout(options?: Options): Observable<any> {
    return this.http.post<any>(`${this.securityUrl}/logout`, {}, options);
  }

  getOnboardingCustomer(
    userId: string,
    options?: Options
  ): Observable<UserCustomerDTO> {
    const url = `${this.apiUrl}/v1/onboarding/customers/${userId}`;

    return this.http.get<UserCustomerDTO>(url, options);
  }

  putOnboardingCustomer(
    userId: string,
    body: UserCustomerUpdateDTO,
    options?: Options
  ): Observable<any> {
    const url = `${this.apiUrl}/v1/onboarding/customers/${userId}`;

    return this.http.put<any>(url, body, options);
  }

  postPositionSave(
    body: PositionSaveRequestDTO,
    options?: Options
  ): Observable<string> {
    const url = `${this.apiUrl}/v1/positions/save`;

    return this.http
      .post<string>(url, body, options)
      .pipe(map(res => this.parser.savePosition(res)));
  }

  getPersonalPositions(options?: Options): Observable<PositionDTO[]> {
    const url = `${this.apiUrl}/v1/personal/positions`;

    return this.http
      .get<PositionDTO[]>(url, options)
      .pipe(map(res => this.parser.personalPositions(res)));
  }

  getPersonalTasks(options?: Options): Observable<TaskListDTO> {
    const url = `${this.apiUrl}/v1/personal/tasks`;

    return this.http.get<TaskListDTO>(url, options);
  }

  getPosition(positionId: string, options?: Options): Observable<PositionDTO> {
    const url = `${this.apiUrl}/v1/positions/${positionId}`;

    return this.http
      .get<PositionDTO>(url, options)
      .pipe(map(res => this.parser.position(res)));
  }

  getPositionFull(
    positionId: string,
    options?: Options
  ): Observable<PositionDTO> {
    const url = `${this.apiUrl}/v1/positions/full/${positionId}`;

    return this.http
      .get<PositionDTO>(url, options)
      .pipe(map(res => this.parser.position(res)));
  }

  postPositionWithdraw(
    positionId: string,
    body: { reasonCode: string }
  ): Observable<any> {
    const url = `${this.apiUrl}/v1/positions/${positionId}/WITHDRAW_REQUEST`;

    return this.http.post<any>(url, body);
  }

  getWizard(
    positionId: string,
    wizardCode: string,
    options?: Options
  ): Observable<WizardDTO> {
    const url = `${this.apiUrl}/v1/positions/${positionId}/wizards/${wizardCode}`;

    return this.http
      .get<WizardDTO>(url, options)
      .pipe(map(res => this.parser.wizard(res)));
  }

  putWizardStepNext(
    positionId: string,
    wizardCode: string,
    stepCode: string,
    options?: Options
  ): Observable<WizardStepNextOutputDTO> {
    const url = `${this.apiUrl}/v1/positions/${positionId}/wizards/${wizardCode}/steps/${stepCode}/next`;

    return this.http.put<WizardStepNextOutputDTO>(url, {}, options);
  }

  putWorkflowTaskClose(
    positionId: string,
    stepCode: string,
    options?: Options
  ): Observable<WizardStepNextOutputDTO> {
    const url = `${this.apiUrl}/v1/workflow/tasks/close/${positionId}/step/${stepCode}`;

    return this.http
      .put<any>(url, {}, options)
      .pipe(map(res => this.parser.workflowTaskClose(res)));
  }

  getSurvey(
    positionId: string,
    surveyCode: string,
    options?: Options
  ): Observable<SurveyDTO[]> {
    const url = `${this.apiUrl}/v1/positions/${positionId}/surveys/${surveyCode}`;

    return this.http
      .get<SurveyDTO[]>(url, options)
      .pipe(map(res => this.parser.surveys(res)));
  }

  putSurvey(
    positionId: string,
    surveyCode: string,
    body: SurveyUpdateDTO,
    options?: Options
  ) {
    const url = `${this.apiUrl}/v1/positions/${positionId}/surveys/${surveyCode}`;

    return this.http.put<any>(url, body, options);
  }

  getForm(
    positionId: string,
    formCode: string,
    options?: Options
  ): Observable<FormDTO[]> {
    const url = `${this.apiUrl}/v1/positions/${positionId}/forms/${formCode}`;

    return this.http
      .get<FormDTO[]>(url, options)
      .pipe(map(res => this.parser.forms(res)));
  }

  postForm(
    positionId: string,
    formCode: string,
    body: FormUpdateDTO,
    options?: Options
  ): Observable<any> {
    const url = `${this.apiUrl}/v1/positions/${positionId}/forms/${formCode}`;

    return this.http.post<any>(url, body, options);
  }

  getChecklist(
    positionId: string,
    options?: Options
  ): Observable<ChecklistDTO> {
    const url = `${this.apiUrl}/v1/positions/${positionId}/checklists/current`;

    return this.http
      .get<ChecklistDTO>(url, options)
      .pipe(map(res => this.parser.checklist(res)));
  }

  getChecklistDocumentDownload(
    positionId: string,
    docId: string,
    options?: Options
  ): Observable<any> {
    const url = `${this.apiUrl}/v1/positions/${positionId}/checklists/current/documents/${docId}/download`;

    return this.http.get(url, options);
  }

  postChecklistDocumentUpload(
    positionId: string,
    docId: string,
    body: FormData,
    options?: Options
  ): Observable<number> {
    const url = `${this.apiUrl}/v1/positions/${positionId}/checklists/current/documents/${docId}/file`;

    return this.http.post<number>(url, body, options);
  }

  putChecklistDocumentOverride(
    positionId: string,
    docId: string,
    fileId: string,
    body: FormData,
    options?: Options
  ): Observable<number> {
    const url = `${this.apiUrl}/v1/positions/${positionId}/checklists/current/documents/${docId}/file/${fileId}`;

    return this.http.put<number>(url, body, options);
  }

  getChecklistDocumentScan(
    positionId: string,
    docId: string,
    options?: Options
  ): Observable<HawkScanDTO> {
    const url = `${this.apiUrl}/v1/positions/${positionId}/checklists/current/documents/${docId}/scan
    `;

    return this.http.get<HawkScanDTO>(url, options);
  }

  getReports(printType: string, options?: Options): Observable<ReportDTO[]> {
    const url = `${this.apiUrl}/v1/reports/${printType}`;

    return this.http.get<ReportDTO[]>(url, options);
  }

  postReportModule(body: any, options?: Options): Observable<any> {
    const url = `${this.apiUrl}/v1/reports/module`;

    return this.http.post<any>(url, body, options);
  }

  postReport(
    positionId: string,
    reportCode: string,
    body: ReportRequestDTO,
    options?: Options
  ): Observable<any> {
    const url = `${this.apiUrl}/v1/positions/${positionId}/reports/${reportCode}`;

    return this.http.post<any>(url, body, options);
  }

  postIdentificationStart(
    method: string,
    body: {
      businessId: string;
      ndg: string;
    },
    options?: Options
  ): Observable<IdentificationLinkDTO> {
    const url = `${this.apiUrl}/v1/identifications/${method}/start`;

    return this.http.post<IdentificationLinkDTO>(url, body, options);
  }

  getIdentificationVerify(
    method: string,
    options?: Options
  ): Observable<IdentificationVerifyDTO> {
    const url = `${this.apiUrl}/v1/identifications/${method}/verify`;

    return this.http.get<IdentificationVerifyDTO>(url, options);
  }

  getDigitalSignatureRequestDsp(
    positionId: string,
    options?: Options
  ): Observable<DigitalSigningDTO> {
    const url = `${this.apiUrl}/v1/positions/${positionId}/digital-signature/request-dsp`;

    return this.http.get<DigitalSigningDTO>(url, options);
  }

  getDigitalSignatureRequestStatusDsp(
    positionId: string,
    options?: Options
  ): Observable<DigitalSigningDTO> {
    const url = `${this.apiUrl}/v1/positions/${positionId}/digital-signature/request-status-dsp`;

    return this.http.get<DigitalSigningDTO>(url, options);
  }

  getDigitalSignatureSign(
    positionId: string,
    options?: Options
  ): Observable<DigitalSigningDTO> {
    const url = `${this.apiUrl}/v1/positions/${positionId}/digital-signature/sign`;

    return this.http.get<DigitalSigningDTO>(url, options);
  }

  getBankAccountEvaluationStart(
    positionId: string,
    options?: Options
  ): Observable<BankAccountLinkDTO> {
    const url = `${this.apiUrl}/v1/positions/${positionId}/psd2/start`;

    return this.http.get<BankAccountLinkDTO>(url, options);
  }

  getBankAccountEvaluationVerify(
    positionId: string,
    correlationId: string,
    options?: Options
  ): Observable<BankAccountEvaluationDTO> {
    const url = `${this.apiUrl}/v1/positions/${positionId}/psd2/verify/${correlationId}`;

    return this.http.get<BankAccountEvaluationDTO>(url, options);
  }

  getQuotationBoundaries(
    positionId: string,
    options?: Options
  ): Observable<QuotationBoundariesDTO> {
    const url = `${this.apiUrl}/v1/quotations/${positionId}/boundaries`;

    return this.http
      .get<QuotationBoundariesDTO>(url, options)
      .pipe(map(res => this.parser.quotationBoundaries(res)));
  }

  postQuotation(
    positionId: string,
    body: QuotationParamsDTO,
    options?: Options
  ): Observable<QuotationDTO> {
    const url = `${this.apiUrl}/v1/quotations/${positionId}`;

    return this.http
      .post<QuotationDTO>(url, body, options)
      .pipe(map(res => this.parser.calculateQuotation(res)));
  }

  putQuotation(
    positionId: string,
    body: QuotationParamsDTO,
    options?: Options
  ): Observable<void> {
    const url = `${this.apiUrl}/v1/quotations/${positionId}`;

    return this.http.put<void>(url, body, options);
  }

  putObligationConfirm(
    positionId: string,
    obligationId: string,
    body: { isConfirmed: boolean },
    options?: Options
  ): Observable<any> {
    const url = `${this.apiUrl}/v1/obligation/${positionId}/confirm/${obligationId}`;

    return this.http.put<any>(url, body, options);
  }

  getNotes(
    positionId: string,
    type: string,
    options?: Options
  ): Observable<NoteDTO[]> {
    const url = `${this.apiUrl}/v1/note/${positionId}/${type}`;

    return this.http
      .get<NoteDTO[]>(url, options)
      .pipe(map(res => this.parser.notes(res)));
  }

  getAtc(options?: Options): Observable<AtcDTO[]> {
    const url = `${this.apiUrl}/v1/atc`;

    return this.http
      .get<AtcDTO[]>(url, options)
      .pipe(map(res => this.parser.atc(res)));
  }

  postAtc(
    atcId: number,
    positionId: string,
    body: AtcDTO,
    options?: Options
  ): Observable<any> {
    const url = `${this.apiUrl}/v1/atc/${atcId}/${positionId}`;

    return this.http.post<any>(url, body, options);
  }

  postContactSendOtp(
    positionId: string,
    channelId: string,
    body: { contact: string },
    options?: Options
  ): Observable<any> {
    const url = `${this.apiUrl}/v1/contacts/positions/${positionId}/channel/${channelId}`;

    return this.http.post<any>(url, body, options);
  }

  putContactVerifyOtp(
    positionId: string,
    channelId: string,
    body: { contact: string; otp: string },
    options?: Options
  ): Observable<any> {
    const url = `${this.apiUrl}/v1/contacts/positions/${positionId}/channel/${channelId}`;

    return this.http.put<any>(url, body, options);
  }

  getNotifications(
    positionId: string,
    options?: Options
  ): Observable<NotificationDTO[]> {
    const url = `${this.apiUrl}/v1/notifications/${positionId}`;

    return this.http
      .get<NotificationDTO[]>(url, options)
      .pipe(map(res => this.parser.notifications(res)));
  }

  putNotification(
    notificationId: string,
    outcome: string,
    body: NotificationDTO,
    options?: Options
  ): Observable<void> {
    const url = `${this.apiUrl}/v1/notifications/${notificationId}/${outcome}`;

    return this.http.put<void>(url, body, options);
  }

  getQuotationCreditRequest(
    mode: string,
    options?: Options
  ): Observable<CreditRequestDTO> {
    const url = `${this.apiUrl}/v1/quotations/calculate/${mode}/creditrequest`;

    return this.http
      .get<CreditRequestDTO>(url, options)
      .pipe(map(res => this.parser.quotationCreditRequest(res)));
  }

  getQuotationCreditUse(
    mode: string,
    options?: Options
  ): Observable<CreditUseListDTO> {
    const url = `${this.apiUrl}/v1/quotations/calculate/${mode}/credituse`;

    return this.http
      .get<CreditUseListDTO>(url, options)
      .pipe(map(res => this.parser.quotationCreditUse(res)));
  }

  postPostselling(
    positionId: string,
    taskCode: string,
    body: PostsellingCreateDTO,
    options?: Options
  ): Observable<any> {
    const url = `${this.apiUrl}/v1/postselling/${positionId}/${taskCode}`;

    return this.http.post<any>(url, body, options);
  }
}
